import { useLayoutEffect } from "react";

const SERIAL = /^(s\.?\s*no\.?|sr\.?\s*no\.?)$/i;
const ACTION = /^(view|action|actions)$/i;
const SELECTOR = ".portal-content table, .MuiDialog-root table";
const MAX_VISIBLE = 5;
const PRIMARY_COUNT = 4;
const TOGGLE_HEIGHT = 44; // must match the tr::after height in index.css

const setFlag = (el, name, on) => {
  if (on) {
    if (!el.hasAttribute(name)) el.setAttribute(name, "");
  } else if (el.hasAttribute(name)) {
    el.removeAttribute(name);
  }
};

const headingText = (th) =>
  (th.textContent || "").replace(/!/g, "").replace(/\s+/g, " ").trim();

const labelTable = (table) => {
  if (table.hasAttribute("data-no-mobile-cards")) return;
  const headRows = table.tHead?.rows;
  if (!headRows?.length) return;

  const labels = [];
  for (const th of headRows[headRows.length - 1].cells) {
    for (let i = 0; i < (th.colSpan || 1); i++) labels.push(headingText(th));
  }
  table.classList.add("mobile-card-table");
  const primaryList = (table.dataset.mobilePrimary || "")
    .split(",")
    .map((l) => l.trim())
    .filter(Boolean);

  for (const body of table.tBodies) {
    for (const row of body.rows) {
      const isEmptyRow = row.cells.length === 1 && row.cells[0].colSpan > 1;
      setFlag(row, "data-mc-empty", isEmptyRow);
      if (isEmptyRow) continue;

      let col = 0;
      const fields = [];
      for (const cell of row.cells) {
        const label = labels[col] ?? "";
        if (cell.getAttribute("data-label") !== label) {
          cell.setAttribute("data-label", label);
        }
        const hide = SERIAL.test(label) || (col === 0 && !label);
        const action = ACTION.test(label);
        setFlag(cell, "data-mc-hide", hide);
        setFlag(cell, "data-mc-action", action);
        if (!hide && !action) fields.push(cell);
        col += cell.colSpan || 1;
      }

      const collapsible = fields.length > MAX_VISIBLE;
      setFlag(row, "data-mc-collapsible", collapsible);
      fields.forEach((cell, i) => {
        const label = cell.getAttribute("data-label");
        const listed = primaryList.includes(label);
        const primary = primaryList.length ? listed : i < PRIMARY_COUNT;
        setFlag(cell, "data-mc-primary", listed);
        setFlag(cell, "data-mc-extra", collapsible && !primary);
      });
    }
  }
};

const useMobileTableCards = () => {
  useLayoutEffect(() => {
    const run = () => {
      document.querySelectorAll(SELECTOR).forEach(labelTable);
    };

    const onClick = (e) => {
      const row = e.target;
      if (
        row.tagName !== "TR" ||
        !row.hasAttribute("data-mc-collapsible") ||
        !window.matchMedia("(max-width: 767px)").matches ||
        e.clientY < row.getBoundingClientRect().bottom - TOGGLE_HEIGHT
      ) {
        return;
      }
      setFlag(row, "data-mc-open", !row.hasAttribute("data-mc-open"));
    };

    run();
    document.addEventListener("click", onClick);
    // Only childList changes are observed, so our own attribute writes don't retrigger it.
    const observer = new MutationObserver(run);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      document.removeEventListener("click", onClick);
    };
  }, []);
};

export default useMobileTableCards;
